import React, { useEffect, useMemo, useState, useRef } from "react";

import { Label, LeafIcon, LoadingIcon } from "@packages/ds";
import { useIntl } from "react-intl";

import { useClickOutside } from "Hooks/useClickOutside";
import {
  getRegions,
  isGreenRegion,
  regionsByZoneSelector,
  regionsLoadingSelector
} from "Reducers/project/region";
import { setupOptionsRegionsSelector } from "Reducers/setupOptions/setupOptions.selectors";
import { useAppSelector, useAppDispatch } from "Store/hooks";

import EnvironmentImpact from "../EnvironmentImpact";

import * as S from "./RegionPicker.styles";

import type { Region } from "@packages/client";

type ListOption = {
  label: string;
  value: string;
  options: Region[];
};

const Events = Object.freeze({
  ArrowUp: "ArrowUp",
  ArrowDown: "ArrowDown",
  Enter: "Enter"
});

const filterListOptions = (keyword: string, listOptions: ListOption[]) => {
  return listOptions
    .map(zone => ({
      ...zone,
      options: zone.options.filter(region =>
        region?.label?.toLowerCase().includes(keyword.toLowerCase())
      )
    }))
    .filter(zone => zone.options.length !== 0);
};

const RegionPicker = ({
  className = "",
  onChange,
  region
}: {
  className?: string;
  onChange: (region?: Region) => void;
  region: Region;
}) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const regionOptions = useAppSelector(setupOptionsRegionsSelector);
  const regions = useAppSelector(regionsByZoneSelector);
  const regionsListIsLoading = useAppSelector(regionsLoadingSelector);

  const inputWrapperRef = useRef(null);
  const itemsRef = useRef<any>([0]);
  const [focusedIndex, setFocusedIndex] = useState(0);

  const [regionValue, setRegionValue] = useState("");
  const [isRegionPickerOpen, setIsRegionPickerOpen] = useState(false);
  const [regionDetails, setRegionDetails] = useState("");
  const regionPickerRef = useRef(null);
  const regionPickerInputRef = useRef<HTMLInputElement>(null);

  useClickOutside(regionPickerRef, () => {
    setRegionDetails("");
    setIsRegionPickerOpen(false);
  });

  useClickOutside(regionPickerRef, () => {
    setRegionDetails("");
    setIsRegionPickerOpen(false);
  });

  const listOptions = useMemo(() => {
    const restrictedRegions = regionOptions;

    const list: ListOption[] = [];
    Object.keys(regions).forEach(zone => {
      const optionsForZone = (
        restrictedRegions.length === 0
          ? regions[zone]
          : regions[zone].filter(region => {
              // The ID of the region for British Council was not done the same as others.
              // Moving forward, all regions have the ID of their fully qualified URL. For now
              // it is easier to hard code this one exception than change the ID for a whole region.
              return (
                (region.id === "bc" &&
                  restrictedRegions.indexOf("bc.platform.sh") !== -1) ||
                restrictedRegions.indexOf(region.id) !== -1
              );
            })
      ).map(elt => {
        const label = elt.provider?.name
          ? `${elt.label} - ${elt.provider?.name}`
          : elt.label;
        return Object.assign({}, elt, { label });
      });

      list.push({
        label: zone === "" ? "Other" : zone,
        value: zone === "" ? "Other" : zone,
        options: optionsForZone
      });
    });

    return filterListOptions(regionValue, list);
  }, [regionOptions, regionValue, regions]);

  useEffect(() => {
    dispatch(getRegions());
  }, [dispatch]);

  useEffect(() => {
    if (itemsRef.current[focusedIndex]) {
      itemsRef.current[focusedIndex].focus();
    }
  }, [focusedIndex]);

  const handleKey = (event, keyboardDirection) => {
    if (event.key === Events.Enter || event.key === " ") {
      setFocusedIndex(focusedIndex + 1);
      setIsRegionPickerOpen(true);
      event.preventDefault();
    }
    if (keyboardDirection === Events.ArrowUp) {
      setFocusedIndex(focusedIndex - 1);
      event.preventDefault();
    } else if (keyboardDirection === Events.ArrowDown) {
      setFocusedIndex(focusedIndex + 1);
      event.preventDefault();
    }
  };

  const handleItemKeyDown = (event, index) => {
    if (event.key === Events.ArrowDown) {
      setFocusedIndex((index + 1) % itemsRef.current.length);
      event.preventDefault();
    } else if (event.key === Events.ArrowUp) {
      setFocusedIndex(
        (index - 1 + itemsRef.current.length) % itemsRef.current.length
      );
      event.preventDefault();
    } else if (event.key === Events.Enter) {
      itemsRef.current[index].click();
      event.preventDefault();
    }
  };

  return (
    <S.Wrapper className={className}>
      <S.Container ref={regionPickerRef}>
        <Label htmlFor="region" required>
          {intl.formatMessage({ id: "region" })}
        </Label>
        <S.InputWrapper
          ref={inputWrapperRef}
          tabIndex={0}
          onKeyDown={e => handleKey(e, Events.ArrowDown)}
          onKeyUp={e => handleKey(e, Events.ArrowUp)}
          onClick={() => {
            setIsRegionPickerOpen(true);
            regionPickerInputRef.current?.focus();
          }}
        >
          <S.Input
            readOnly
            ref={regionPickerInputRef}
            id="region"
            placeholder={intl.formatMessage({
              id: "region_picker.default_text"
            })}
            value={region?.label || regionValue}
            onChange={e => {
              onChange();
              setRegionValue(e.target.value);
              setRegionDetails("");
            }}
            type="text"
            autoComplete="off"
          />
          <S.ChevronIcon isOpen={isRegionPickerOpen} />
        </S.InputWrapper>
        {isRegionPickerOpen && (
          <S.ContentWrapper autoFocus>
            <S.ItemList>
              {listOptions.length !== 0 ? (
                listOptions.map(zone => (
                  <React.Fragment key={zone.value}>
                    <S.Header>{zone.label}</S.Header>
                    {zone.options.map((currentRegion, idx) => (
                      <S.Item
                        tabIndex={0}
                        key={currentRegion.id}
                        $isSelected={currentRegion.id === region?.id}
                        ref={el => (itemsRef.current[idx] = el)}
                        onKeyDown={event => handleItemKeyDown(event, idx)}
                        onMouseOver={() => setRegionDetails(currentRegion.id)}
                        onClick={() => {
                          setIsRegionPickerOpen(false);
                          onChange(currentRegion);
                        }}
                      >
                        {currentRegion.label}{" "}
                        {isGreenRegion(currentRegion) && (
                          <S.LeafIconWrapper>
                            <LeafIcon
                              color="var(--theme-success-600)"
                              width={16}
                              height={16}
                            />
                          </S.LeafIconWrapper>
                        )}
                      </S.Item>
                    ))}
                  </React.Fragment>
                ))
              ) : regionsListIsLoading === true ? (
                <S.LoadingWrapper>
                  <LoadingIcon />
                </S.LoadingWrapper>
              ) : (
                <S.NoOptionsContainer>
                  {intl.formatMessage({
                    id: "no_options"
                  })}
                </S.NoOptionsContainer>
              )}
            </S.ItemList>
            <S.ItemDetails>
              <EnvironmentImpact
                regionId={regionDetails}
                shouldShowAllLeafs={true}
              />
            </S.ItemDetails>
          </S.ContentWrapper>
        )}
      </S.Container>
      <EnvironmentImpact regionId={region?.id} shouldShowAllLeafs={true} />
    </S.Wrapper>
  );
};

export default RegionPicker;
